import React, { useState, useEffect } from 'react';
import { VStack, HStack, Text, Button } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useChangeUserRolesMutation, useGetUserByIdQuery } from '../api';
import { getAssignableRoles } from '../constants';

interface ChangeRolesModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const ChangeRolesModal: React.FC<ChangeRolesModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [error, setError] = useState('');
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });
  
  const [changeRoles, { isLoading }] = useChangeUserRolesMutation();

  // Initialize with current roles
  useEffect(() => {
    if (user && user.roles) {
      setSelectedRoles(user.roles);
    }
  }, [user]);

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev => {
      if (prev.includes(role)) {
        return prev.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleSubmit = async () => {
    if (!user) return;
    
    setError('');
    
    if (selectedRoles.length === 0) {
      setError('Please select at least one role');
      return;
    }

    try {
      await changeRoles({
        id: user.id,
        roles: selectedRoles,
      }).unwrap();
      
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.data?.message || 'Failed to change roles');
    }
  };

  const handleClose = () => {
    if (user) {
      setSelectedRoles(user.roles || []);
    }
    setError('');
    onClose();
  };

  const hasChanges = user && JSON.stringify([...selectedRoles].sort()) !== JSON.stringify([...(user.roles || [])].sort());

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle>Change User Roles</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        <VStack gap={4} align="stretch">
          {user && (
            <Text fontSize="sm" color="gray.600">
              Changing roles for: <strong>{user.first_name} {user.last_name}</strong>
            </Text>
          )}
          
          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
              Current Roles
            </Text>
            <Text fontSize="sm" color="gray.700">
              {user?.roles?.join(', ') || 'No roles assigned'}
            </Text>
          </div>
          
          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
              Select New Roles <Text as="span" color="red.500">*</Text>
            </Text>
            <VStack gap={2} align="stretch">
              {getAssignableRoles().map((role) => (
                <label key={role.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.value)}
                    onChange={() => handleRoleToggle(role.value)}
                  />
                  <Text>{role.label}</Text>
                </label>
              ))}
            </VStack>
          </div>
          
          {error && (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          )}
          
          <Text fontSize="sm" color="gray.600">
            The user's permissions will be updated based on the selected roles.
          </Text>
        </VStack>
      </DrawerBody>
      
      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading || !hasChanges}
            colorPalette="teal"
          >
            Update Roles
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(ChangeRolesModal, {
  action: 'change-roles',
});
